import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Descriptions, Table, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  EditOutlined,
  HistoryOutlined,
  RetweetOutlined,
} from '@ant-design/icons';

import styles from '../styles';
import {
  getAccessoryInventory,
  clearAccessoryInventory,
  getAccessoryTransferHistory,
  clearAccessoryTransferHistory,
  clearAccessoryInventoryStats,
  getAccessoryInventoryStats,
} from '../Actions';
import { sorter, onFilter, filterDropdown, filterIcon, get } from '../utils';
import Breadcrumbs from '../Components/Breadcrumbs';
import constants from '../constants';
import TransferHistory from './TransferHistory';

const InventoryList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  const accessoryInventory = useSelector(state => state.accessoryInventory);
  const accessoryInventoryStats = useSelector(
    state => state.accessoryInventoryStats
  );
  const accessoryTransferHistory = useSelector(
    state => state.accessoryTransferHistory
  );

  const [recordId, setRecordId] = useState(null);
  const [transferHistoryModal, setTransferHistoryModal] = useState(false);

  useEffect(() => {
    dispatch(getAccessoryInventory());
    dispatch(getAccessoryInventoryStats());
    return () => {
      dispatch(clearAccessoryInventory());
      dispatch(clearAccessoryInventoryStats());
    };
  }, []);

  useEffect(() => {
    if (recordId && transferHistoryModal) {
      dispatch(getAccessoryTransferHistory({ accessoryInventoryId: recordId }));
    }
  }, [recordId, transferHistoryModal]);

  const showModal = useCallback(id => {
    setRecordId(id);
    setTransferHistoryModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setRecordId(null);
    setTransferHistoryModal(false);
    dispatch(clearAccessoryTransferHistory());
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(
            constants.permissions.modifyAccessoryInventory
          ) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/inventory/accessory/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const transferColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(
            constants.permissions.modifyAccessoryInventory
          ) ? (
            <Tooltip placement="bottom" title="Transfer">
              <Link to={`/inventory/accessory/transfer/${record.id}`}>
                <RetweetOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const transferHistoryColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(
            constants.permissions.accessoryInventoryTransferAuthorization
          ) && record.transferCount > 0 ? (
            <Tooltip placement="bottom" title="History">
              <HistoryOutlined
                className={classes.tableIcon}
                onClick={() => showModal(record.id)}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const _columns = [
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
        sorter: sorter('accessory'),
        onFilter: onFilter('accessory'),
        filterDropdown,
        filterIcon,
        width: '15%',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        sorter: sorter('unit'),
        onFilter: onFilter('unit'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Factory',
        dataIndex: 'factory',
        sorter: sorter('factory'),
        onFilter: onFilter('factory'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Rack #',
        dataIndex: 'rackno',
        sorter: sorter('rackno'),
        onFilter: onFilter('rackno'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.modifyAccessoryInventory)) {
      _columns.push(
        {
          title: 'Update',
          render: updateColumn,
        },
        {
          title: 'Transfer',
          render: transferColumn,
        }
      );
    }

    if (
      permissions.includes(
        constants.permissions.accessoryInventoryTransferAuthorization
      )
    ) {
      _columns.push({
        title: 'History',
        render: transferHistoryColumn,
      });
    }

    return _columns;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1600, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Inventory"
        moduleLink="/inventory/accessory"
        subModuleName="Accessory"
      />
      <div style={{ padding: 10 }}>
        <Descriptions
          bordered
          size="small"
          labelStyle={{ fontWeight: 500 }}
          column={{ xxl: 8, xl: 6, lg: 4, md: 3, sm: 2, xs: 1 }}
        >
          {accessoryInventoryStats.map(stats => (
            <Descriptions.Item key={stats.factory} label={stats.factory}>
              {stats.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={accessoryInventory}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
      <TransferHistory
        hideModal={hideModal}
        transferHistoryModal={transferHistoryModal}
        accessoryTransferHistory={accessoryTransferHistory}
      />
    </>
  );
};

InventoryList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(InventoryList);
